// A helper function to streamline fetch requests
async function apiFetch(url, options = {}) {
    // Default options for every request
    const defaultOptions = {
        // Include cookies and session data in the request
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        },
    };

    // Merge custom options with defaults
    const mergedOptions = {
        ...defaultOptions,
        ...options,
        headers: {
            ...defaultOptions.headers,
            ...options.headers,
        },
    };

    // If there's a body, stringify it
    if (mergedOptions.body) {
        mergedOptions.body = JSON.stringify(mergedOptions.body);
    }

    try {
        const response = await fetch(url, mergedOptions);

        // fetch doesn't throw on HTTP errors, so we check response.ok
        if (!response.ok) {
            // Try to parse the error message from the server
            const errorData = await response.json().catch(() => ({ message: response.statusText }));
            throw new Error(errorData.message || 'An unknown server error occurred.');
        }

        // Handle 204 No Content response (e.g., for DELETE requests)
        if (response.status === 204) {
            return;
        }

        // For all other successful responses, parse the JSON body
        return await response.json();

    } catch (error) {
        console.error('API Fetch Error:', error);
        // Re-throw the error so the component's catch block can handle it
        throw error;
    }
}


// --- API Endpoints ---

// Get the base URL from your environment variables
const API_URL = process.env.REACT_APP_API_URL;

// Construct the full base URLs for the different API sections
const ADMIN_API_BASE = `${API_URL}/admin`;
const GENERAL_API_BASE = `${API_URL}`;

// --- Stats ---
export const getSiteOverviewStats = () => apiFetch(`${ADMIN_API_BASE}/stats/overview`);

// --- User Management ---
export const getUsers = (params) => {
    const query = new URLSearchParams(params).toString();
    return apiFetch(`${ADMIN_API_BASE}/users?${query}`);
};
export const updateUser = (userId, data) => {
    return apiFetch(`${ADMIN_API_BASE}/users/${userId}`, {
        method: 'PUT',
        body: data,
    });
};
export const deleteUser = (userId) => {
    return apiFetch(`${ADMIN_API_BASE}/users/${userId}`, {
        method: 'DELETE',
    });
};

// --- Listing Management ---
export const getListings = (params) => {
    const query = new URLSearchParams(params).toString();
    return apiFetch(`${ADMIN_API_BASE}/listings?${query}`);
};
export const getReportedListings = (params) => {
    const query = new URLSearchParams(params).toString();
    return apiFetch(`${ADMIN_API_BASE}/listings/reported?${query}`);
};
export const resolveReport = (reportId) => {
    return apiFetch(`${ADMIN_API_BASE}/listings/reports/${reportId}/resolve`, {
        method: 'POST',
    });
};
export const deleteListing = (listingId) => {
    return apiFetch(`${ADMIN_API_BASE}/listings/${listingId}`, {
        method: 'DELETE',
    });
};

// --- Category Management ---
// Using the general API base URL for these as per your backend setup
export const getCategories = () => apiFetch(`${GENERAL_API_BASE}/categories`);
export const createCategory = (data) => {
    return apiFetch(`${GENERAL_API_BASE}/categories`, {
        method: 'POST',
        body: data,
    });
};
export const updateCategory = (id, data) => {
    return apiFetch(`${GENERAL_API_BASE}/categories/${id}`, {
        method: 'PUT',
        body: data,
    });
};
export const deleteCategory = (id) => {
    return apiFetch(`${GENERAL_API_BASE}/categories/${id}`, {
        method: 'DELETE',
    });
};

// --- Blog Management ---
// All blog management routes are under the /admin path
export const getBlogPosts = () => {
    return apiFetch(`${ADMIN_API_BASE}/blog/posts`);
};

export const createBlogPost = (postData) => {
    return apiFetch(`${ADMIN_API_BASE}/blog/posts`, {
        method: 'POST',
        body: postData,
    });
};

export const updateBlogPost = (postId, postData) => {
    return apiFetch(`${ADMIN_API_BASE}/blog/posts/${postId}`, {
        method: 'PUT',
        body: postData,
    });
};

export const deleteBlogPost = (postId) => {
    return apiFetch(`${ADMIN_API_BASE}/blog/posts/${postId}`, {
        method: 'DELETE',
    });
};

// --- Moderation Management ---

/**
 * Pridobi seznam oglasov, ki čakajo na ročni pregled (moderation_status = 'v_pregledu')
 */
export const getModerationQueue = () => {
    return apiFetch(`${ADMIN_API_BASE}/listings/moderation-queue`);
};

/**
 * Razreši moderacijo oglasa (odobri ali dokončno zavrni)
 * @param {string} listingId - ID oglasa
 * @param {string} decision - 'approve' ali 'reject'
 */
export const resolveModeration = (listingId, decision) => {
    return apiFetch(`${ADMIN_API_BASE}/listings/${listingId}/resolve-moderation`, {
        method: 'POST',
        body: { decision },
    });
};